<template>
  <b-overlay :show="loading" opacity="0.5">
    <div>
      <list-table
        :records="records" 
        :columns="tableColumns"
        :show-import-button="false"
        :total-records="totalRecords"
        :search-filter.sync="searchFilter"
        search-place-holder="Search Pharmacy"
        :current-page-number.sync="currentPage"
        :show-export-button="can('pharmacies:CanExport')"
        @export="onExport"
        @refetchData="fetchAllData"
        @row-clicked="onViewSummary"
      >
        <template #cell(pharmacy)="data">
          <b-media vertical-align="center">
            <template #aside>
              <b-avatar
                size="32"
                :src="getValueFromSource(data.item, 'facility_details.avatar.path')"
                :text="sentenceCase(avatarText(`${getValueFromSource(data.item, 'facility_details.pharmacy_name')}`))"
                :variant="`light-success`"
                :to="{ name: 'admin-pharmacy-details', params: { id: getValueFromSource(data.item, 'pharmacy._id') } }"
              />
            </template>

            <div>
              {{ sentenceCase(getValueFromSource(data.item, 'facility_details.pharmacy_name')) }}
            </div> 

            <span class="text-muted">{{ getValueFromSource(data.item, 'facility_details.pharmacy_email') }}</span><br />
            <span class="text-muted">@{{ getValueFromSource(data.item, 'facility_details.pharmacy_phone') }}</span>
          </b-media>
        </template>

        <template #cell(status)="data">
          <b-badge
            pill
            :variant="`light-${resolveStatusVariant(data.item.status)}`"
            class="text-capitalize"
          >
            {{ getUserStatusText(data.item.status) }}
          </b-badge>
        </template>

        <template #cell(actions)="data">
          <b-dropdown
            v-if="data.item.status === 'pending' || data.item.status === 'declined'"
            variant="link"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>

            <b-dropdown-item
              v-if="data.item.status === 'pending' || data.item.status === 'declined' && can('pharmacies:CanApprove')"
              @click="approvePharmacy(data.item._id)"
            >
              <feather-icon icon="UserCheckIcon" />
              <span class="align-middle ml-50">Approve</span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="data.item.status === 'pending' && can('pharmacies:CanDecline')"
              @click="declinePharmacy(data.item._id)"
            >
              <feather-icon icon="UserXIcon" />
              <span class="align-middle ml-50">Decline</span>
            </b-dropdown-item>
          </b-dropdown>
          <span v-else>-</span>
        </template>
      </list-table>
    </div>
  </b-overlay>
</template>

<script>
import {
  BOverlay, BCard, BRow, BCol, BAlert, BLink, BTable,
  BMedia, BAvatar, BButton, BFormFile,
  BBadge, BDropdown, BDropdownItem, BPagination,
  BInputGroup, BInputGroupAppend, BFormInput
} from 'bootstrap-vue'

import { get, debounce } from 'lodash';

import vSelect from 'vue-select';
//   import useJwt from "@/auth/jwt/useJwt";
import Ripple from 'vue-ripple-directive';
import FileDownload from "js-file-download";

import ListTable from "@/@core/components/ListTable/ListTable.vue";
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import AddConsultationModal from '@/@core/components/shared/admin/modals/AddConsultationModal.vue';

const watchHandler = {
  handler(){
    this.debouncedFetchUsersData()
  }, 
  immediate: false
}

const watchHandlerAndResetPageNumber = {
  handler(){
    this.currentPage = 1
    this.debouncedFetchUsersData()
  }, 
  immediate: false
}


export default {
  components: {
    ListTable,

    BOverlay,
    BCard,
    BTable,
    BRow,
    BCol,
    BAlert,
    BBadge,
    BLink,
    BMedia,
    BAvatar,
    BButton,
    BDropdown,
    BFormFile,
    BFormInput,
    BPagination,
    BInputGroup, 
    BDropdownItem,
    BInputGroupAppend, 

    vSelect,
    AddConsultationModal
  },
  directives: {
    Ripple,
  },
  data(){
    return {
      loading: false,
      showFilter: false,

      searchFilter: null,

      records: [],
      
      tableColumns: [
        { key: 'pharmacy', label: 'Pharmacy', sortable: false, },
        { key: 'status' },
        { key: 'created', label: 'Date', sortable: false, formatter: val => `${this.formatDate(val)}`, },
        { key: 'actions' },
      ],
      debouncedFetchUsersData: () => {}
    }
  },
  computed: {
    perPage: {
      get() {
        return this.$store.getters[`navigation/recordsPerPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_RECORDS_PER_PAGE`, value)
      }
    },
    currentPage: {
      get() {
        return this.$store.getters[`navigation/currentPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_CURRENT_PAGE_NUMBER`, value)
      }
    },
    totalRecords: {
      get(){
        return this.$store.getters[`navigation/totalRecords`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_TOTAL_RECORDS`, value)
      }
    },
  },
  watch: {
    perPage: {
      handler(){
        this.currentPage = 1;
        this.debouncedFetchUsersData()
      }, 
      immediate: false
    },
    currentPage: watchHandler,
    searchFilter: watchHandlerAndResetPageNumber,
  },
  created(){
    this.debouncedFetchUsersData = debounce(this.fetchAllData, 500);
    this.fetchAllData()
    
    const { 
      page = 1, 
      search = "", 
      limit = this.perPage 
    } = this.$route.query;
    
    this.currentPage = +page;
    this.searchFilter = search;
    this.perPage = +limit;
  },
  methods: {
    async fetchAllData() {
      try {
        this.loading = true; 

        const query = {
          limit: this.perPage,
          page: this.currentPage,
          search: this.searchFilter,
        }
        this.$router.push({ query }).catch(() => {});

        const request = await this.useJwt().mainService.pharmacies.list(query);
        const { data, pagination } = request.data;
        
        this.totalRecords = pagination.totalRecords
        this.perPage = pagination.limit;
        this.records = data;
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        console.log("error_message", error_message)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async approvePharmacy(pharmacy_id) {
      try {
        this.loading = true;

        if (!this.can(`pharmacies:CanApprove`)) {
          return
        }

        const activateConfirmation = await this.$swal({
          title: 'Approve this Pharmacy?',
          text: "Do you want to approve this pharmacy?",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, proceed!',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        })

        if (!activateConfirmation.value){
          return;
        }

        await this.useJwt().mainService.pharmacies.approve(pharmacy_id);

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            variant: 'success',
            icon: 'CheckCircleIcon',
            text: `Pharmacy approved successfully`
          },
        });

        this.fetchAllData()
      } catch (error){
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async declinePharmacy(pharmacy_id) {
      try {
        this.loading = true;

        if (!this.can(`pharmacies:CanDecline`)) {
          return
        }

        const activateConfirmation = await this.$swal({
          title: 'Decline this Pharmacy?',
          text: "Do you want to decline this pharmacy?",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, proceed!',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        })

        if (!activateConfirmation.value){
          return;
        }

        await this.useJwt().mainService.pharmacies.decline(pharmacy_id);

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            variant: 'success',
            icon: 'CheckCircleIcon',
            text: `Pharmacy declined successfully`
          },
        });

        this.fetchAllData()
      } catch (error){
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async onExport(){
      try {
        this.loading = true;

        const result = await this.$swal({
          title: 'Confirm Export?',
          text: "This will export data based on current filters.",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, proceed.',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.value) {
          return;
        }

        const query = {
          search: this.searchFilter,
        }

        const response = await this.useJwt().mainService.pharmacies.export(query);
        FileDownload(response.data, "pharmacies.csv");
      } catch (error) {
        const error_message = get(error, "response.statusText") || error.message;

        this.$nextTick(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        })
      } finally {
        this.loading = false;
      }
    },
    onViewSummary(pharmacy){
      this.$router.push({
        name: 'admin-pharmacy-details',
        params: { id: pharmacy.id }
      })
    },
  }
}
</script>

<style lang="scss" scoped>
  @import '@core/scss/vue/libs/vue-select.scss';
  .width-100 {
    width: 100px;
  }
  .per-page-selector {
    width: 90px;
  }
</style>

<style lang="scss">
  .loan-list-tb-row {
    cursor: pointer !important;
  }
</style>

